import { useContext } from "react";
import { useForm } from "react-hook-form";
import { ActionKey, ObjectType } from "common/utils/actionBar";
import { getUserErrors } from "common-client/utils/apollo";
import { useCreateBulkOperationMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { useDeleteModal } from "../DeleteModal";
import {
  DeleteConfirmationForm,
  DeleteObjectContent,
} from "../DeleteObjectContent";
import { TableRowsSelectionState } from "../Tables/RowSelectionProvider";
import { getTableNameForTracking, pluralizeObjectType } from "./utils";

export function useActionBarDeleteModal({
  state,
  objectType,
  onDelete,
  setLoadingAction,
}: {
  state: TableRowsSelectionState;
  objectType: ObjectType;
  onDelete: () => void;
  setLoadingAction: (loadingAction: ActionKey | null) => void;
}) {
  const { account } = useContext(AuthContext);
  const toast = useStatusToasts();
  const { totalSelected } = state;
  const pluralizedObjectType = pluralizeObjectType(objectType, totalSelected);

  const {
    formState: { isValid },
    control,
  } = useForm<DeleteConfirmationForm>({
    defaultValues: {
      confirmFileDelete: false,
      confirmDashboardRemoval: false,
      confirmPublicWebsiteRemoval: false,
    },
  });

  const [createBulkOperation] = useCreateBulkOperationMutation({
    onCompleted: data => {
      if (data.createBulkOperation.success) {
        const { message, processedCount: totalDeleted } =
          data.createBulkOperation;
        toast.addSuccessToast(
          message ??
            `Successfully deleted ${totalDeleted} ${pluralizedObjectType}`,
        );
      }
    },
    onError: error => {
      const userError = getUserErrors(error);
      if (userError) {
        toast.addErrorToast(userError);
      } else {
        toast.addErrorToast(
          `An error occurred while deleting ${pluralizedObjectType}. Please try again.`,
        );
      }
    },
  });

  const handleDelete = async () => {
    const tableName = getTableNameForTracking(state.queryDescription!);

    track("Bulk action triggered", {
      operation: "delete",
      tableName,
      totalSelected,
    });

    await createBulkOperation({
      variables: {
        data: {
          objectType,
          operation: "delete",
          selectorParameters: {
            accountId: account?.id ?? "",
            mode: state.mode,
            ids: state.ids,
            queryDescription: JSON.stringify(state.queryDescription),
            totalCount: state.totalSelected,
          },
        },
      },
    });
  };

  const [showDeleteModal] = useDeleteModal({
    headerText: `Delete ${pluralizedObjectType}`,
    subheaderText: `${totalSelected} ${pluralizedObjectType}`,
    Content: (
      <DeleteObjectContent
        objectType={pluralizedObjectType}
        control={control}
      />
    ),
    deleteButtonText: "Delete",
    disabled: !isValid,
    onDelete: async () => {
      setLoadingAction("delete");
      try {
        await handleDelete();
        onDelete();
      } finally {
        setLoadingAction(null);
      }
    },
    dependencies: [totalSelected, objectType, onDelete, control, isValid],
  });

  return showDeleteModal;
}
